import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Fisher-Yates shuffle (returns a new array; never mutates the source). Used
// to randomize quiz options and word-bank chips so the correct answer isn't
// always in the same authored position.
export function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Minor words that stay lowercase in title case (unless first/last word).
const TITLE_MINOR = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'into', 'nor',
  'of', 'on', 'onto', 'or', 'over', 'per', 'the', 'to', 'up', 'via', 'vs', 'with',
  'is', 'are', 'be', 'it',
]);

/**
 * Title-case a string while keeping common minor words lowercase and leaving
 * existing all-caps acronyms (UI, UX, API, HTML) intact.
 * "what is code" → "What is Code", "build the prompt" → "Build the Prompt".
 */
export function titleCase(input: string): string {
  if (!input) return input;
  const words = input.trim().split(/(\s+)/); // keep whitespace tokens
  const realIdx: number[] = [];
  words.forEach((w, i) => { if (w.trim()) realIdx.push(i); });
  const firstReal = realIdx[0];
  const lastReal = realIdx[realIdx.length - 1];

  return words
    .map((w, i) => {
      if (!w.trim()) return w; // whitespace
      // Preserve tokens that already contain an uppercase letter (acronyms, brands).
      if (/[A-Z]/.test(w)) return w;
      const lower = w.toLowerCase();
      const bare = lower.replace(/[^a-z]/g, '');
      if (i !== firstReal && i !== lastReal && TITLE_MINOR.has(bare)) return lower;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join('');
}
